import type { InputHTMLAttributes, KeyboardEvent } from 'react'
import { useEffect, useId, useRef, useState } from 'react'

import { FieldError } from './ui'

/**
 * A text field that offers matching values as the underwriter types, which is
 * what the legacy K2 form does under State, Postcode and Suburb.
 *
 * It stays a text field: anything can be typed, whether or not it is offered.
 * The list is a shortcut, never a constraint, so a lookup that fails leaves an
 * ordinary input behind rather than blocking the search.
 */

/** Wait after the last keystroke before asking the API, so typing is not chatty. */
const DEBOUNCE_MS = 180

interface ComboFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'list'> {
  label: string
  value: string
  onValueChange: (value: string) => void
  /** Resolves the values to offer for a term. Rejections are ignored. */
  onSuggest: (term: string, signal: AbortSignal) => Promise<string[]>
  /** Changes whenever another criterion that narrows this list changes. */
  contextKey?: string
  /**
   * Increment to force the list shut. The form does this on submit: pressing
   * Enter in the field leaves it focused, so blur alone would leave the list
   * hanging over the results the same keystroke just fetched.
   */
  closeToken?: number
  error?: string
}

export function ComboField({
  label,
  value,
  onValueChange,
  onSuggest,
  contextKey = '',
  closeToken = 0,
  error,
  ...rest
}: ComboFieldProps) {
  const id = useId()
  const listId = `${id}-suggestions`
  const errorId = `${id}-error`

  const [options, setOptions] = useState<string[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  // The first lookup after the API process starts pays for the connection, the
  // managed identity token and an unfiltered list, so it can take seconds while
  // every later one is a cache hit. Without this the field looks broken rather
  // than busy, which is exactly how it was reported.
  const [isLoading, setIsLoading] = useState(false)
  /**
   * True when the last lookup failed, as opposed to returning nothing.
   *
   * Those two were indistinguishable on screen, and that cost two rounds of
   * diagnosis. A lookup that cannot be answered was designed to leave a plain
   * text field, which is right for parity: the list is a shortcut, not a
   * constraint, and the field still works. But it also meant a broken lookup
   * looked exactly like a suburb with no matches, so "the lookups are not
   * working" could not be told apart from "there is nothing to suggest".
   */
  const [didFail, setDidFail] = useState(false)
  // Only the field being typed in may open a list. Without this, changing one
  // criterion changed every other field's contextKey, which re-ran their
  // lookups and popped all three lists open at once.
  const [isFocused, setIsFocused] = useState(false)

  const listRef = useRef<HTMLUListElement>(null)
  // Selecting a value sets it, and that must not reopen the list.
  const wantsSuggestions = useRef(false)
  // Held in a ref so a new callback identity on every keystroke, which is what
  // the parent produces, does not restart the lookup.
  const suggest = useRef(onSuggest)
  suggest.current = onSuggest

  const term = value.trim()

  // Submitting the form shuts the list, whether or not the field kept focus.
  useEffect(() => {
    if (closeToken === 0) return
    wantsSuggestions.current = false
    setIsOpen(false)
    setActiveIndex(-1)
    setIsLoading(false)
  }, [closeToken])

  useEffect(() => {
    if (!wantsSuggestions.current || !isFocused || !term) {
      setOptions([])
      setIsOpen(false)
      setActiveIndex(-1)
      setIsLoading(false)
      setDidFail(false)
      return
    }

    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      setIsLoading(true)
      suggest
        .current(term, controller.signal)
        .then((items) => {
          if (controller.signal.aborted) return
          setIsLoading(false)
          setDidFail(false)
          // Blur does not abort the request, so a slow lookup could previously
          // land after the underwriter had moved on and reopen the list over
          // whatever they were doing. The ref says whether they still want it.
          if (!wantsSuggestions.current) return
          setOptions(items)
          setActiveIndex(-1)
          setIsOpen(items.length > 0)
        })
        .catch((error: unknown) => {
          // The field stays usable: it is free text and always was, so a failed
          // lookup must not block a search. But it now says so, rather than
          // looking identical to a term with no matches.
          if (controller.signal.aborted) return
          setIsLoading(false)
          setOptions([])
          setIsOpen(false)
          setDidFail(true)
          // The only place this is diagnosable from a browser. A blocked
          // request, a rejected token and an empty list are three different
          // problems that looked the same on screen.
          console.warn(`Peril lookup failed for ${label}:`, error)
        })
    }, DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
      setIsLoading(false)
    }
  }, [term, contextKey, isFocused])

  // Keeps the keyboard's position visible in a scrolling list.
  useEffect(() => {
    if (activeIndex < 0) return
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  function close() {
    wantsSuggestions.current = false
    setIsOpen(false)
    setActiveIndex(-1)
    setIsLoading(false)
    setDidFail(false)
  }

  function select(option: string) {
    wantsSuggestions.current = false
    onValueChange(option)
    setIsOpen(false)
    setActiveIndex(-1)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!isOpen || options.length === 0) return
      event.preventDefault()
      const last = options.length - 1
      setActiveIndex((current) => {
        if (event.key === 'ArrowDown') return current >= last ? 0 : current + 1
        return current <= 0 ? last : current - 1
      })
      return
    }
    // Enter takes the highlighted value. With nothing highlighted it submits the
    // form, as it does in every other field.
    const active = activeIndex >= 0 ? options[activeIndex] : undefined
    if (event.key === 'Enter' && isOpen && active !== undefined) {
      event.preventDefault()
      select(active)
      return
    }
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault()
      close()
      return
    }
    if (event.key === 'Tab' && isOpen) close()
  }

  return (
    <div className="field combo">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`field__control${error ? ' field__control--invalid' : ''}`}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        autoComplete="off"
        value={value}
        onChange={(event) => {
          wantsSuggestions.current = true
          onValueChange(event.target.value)
        }}
        onFocus={() => setIsFocused(true)}
        // Typing opens the list. Focusing a field that is already filled does
        // not, since its own value is all it would offer.
        onBlur={() => {
          setIsFocused(false)
          close()
        }}
        onKeyDown={handleKeyDown}
        {...rest}
      />
      {/* Shown only while a lookup is genuinely outstanding and there is nothing
          to show yet. Once values are on screen a refresh happens underneath
          them, so the list does not flicker on every keystroke. */}
      {isLoading && !isOpen ? <p className="combo__status">Searching</p> : null}
      {/* Not an error callout: the field works, so this is a note rather than a
          failure. Shown only while the field is focused, so a stale message does
          not sit under a field nobody is using. */}
      {didFail && !isLoading && !isOpen ? (
        <p className="combo__status combo__status--failed">
          Suggestions unavailable. You can still type a value.
        </p>
      ) : null}
      {isOpen ? (
        <ul
          className="combo__list"
          id={listId}
          ref={listRef}
          role="listbox"
          aria-label={`${label} suggestions`}
          // Keeps focus in the field, so choosing with the mouse does not blur
          // the list away before the click lands.
          onMouseDown={(event) => event.preventDefault()}
        >
          {options.map((option, index) => (
            <li
              key={option}
              className={`combo__option${index === activeIndex ? ' combo__option--active' : ''}`}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => select(option)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {option}
            </li>
          ))}
        </ul>
      ) : null}
      <FieldError id={errorId} message={error} />
      <span className="visually-hidden" role="status">
        {isOpen
          ? `${options.length} suggestions. Use the arrow keys to choose one.`
          : isLoading
            ? 'Searching for suggestions'
            : didFail
              ? 'Suggestions unavailable. You can still type a value.'
              : ''}
      </span>
    </div>
  )
}
